import classNames from 'classnames';
import bind from 'memoize-bind';
import moment, { Moment } from 'moment-timezone';
import React, { Fragment, PureComponent } from 'react';

import PrevMonthIcon from '@sbu/assets.svg/src/single-chevron-left.svg';
import NextMonthIcon from '@sbu/assets.svg/src/single-chevron-right.svg';
import { Button, IconButton } from '@sumologic/yama-ds-components';

import { CalendarChangeEvent } from './Calendar';
import css from './CalendarDay.module.scss';
import { CalendarNavigation } from './CalendarNavigation';
import { Day, getWeeks } from './getWeeks';

interface CalendarDayProps {
  viewValue: Date;
  active?: Date;
  activeEnd?: Date;
  minDate?: Date;
  maxDate?: Date;
  setFocusInDayView: boolean;
  isRange?: boolean;
  onChange: (event: CalendarChangeEvent) => void;
  onViewChange: (view: 'day' | 'month' | 'year') => void;
  onViewValueChange: (Date) => void;
  onDirectionChange: (direction: 0 | 1 | -1) => void;
}

interface CalendarDayState {
  activeIndex: number;
  hoverDate: Date | undefined;
}

export class CalendarDay extends PureComponent<
  CalendarDayProps,
  CalendarDayState
> {
  state: CalendarDayState = {
    activeIndex: -1,
    hoverDate: undefined,
  };

  componentDidUpdate(prevProps: CalendarDayProps) {
    const { active, viewValue, setFocusInDayView } = this.props;
    const today = moment().startOf('day').toDate();

    if (setFocusInDayView && !prevProps.setFocusInDayView) {
      const offset = moment(viewValue).startOf('month').day();
      this.setState({ activeIndex: offset });
    }

    if (
      active &&
      active !== prevProps.active &&
      active.valueOf() === today.valueOf()
    ) {
      this.setTodayPosition();
    }

    if (
      this.state.activeIndex >= 0 &&
      prevProps.viewValue.valueOf() !== viewValue.valueOf()
    ) {
      this.setFocusPosition();
    }
  }

  setFocus(activeIndex: number) {
    this.setState({ activeIndex });
  }

  setTodayPosition() {
    const indexOfMonth = moment().startOf('day').date() - 1;
    const offset = moment().startOf('month').day();

    this.setState({ activeIndex: indexOfMonth + offset });
  }

  setFocusPosition() {
    const { viewValue, minDate, maxDate } = this.props;
    const { activeIndex } = this.state;
    const numRows = getWeeks(viewValue).length;

    if (activeIndex < 0) {
      this.setFocus(0);
    } else if (minDate || maxDate) {
      // TODO: Fix key navigation given min/max dates; currently
      // force focus on first navigatable element so keyboard nav
      // doesn't break
      this.setFocus(0);
    } else {
      if (activeIndex < 7) {
        this.setFocus(activeIndex + (numRows - 1) * 7);
      } else {
        this.setFocus(activeIndex % 7);
      }
    }
  }

  onDayLeave() {
    this.setState({ hoverDate: undefined });
  }

  onDayHover(day: Day) {
    const { active, activeEnd, isRange } = this.props;

    if (!isRange || (active && activeEnd) || (!active && !activeEnd)) {
      this.setState({ hoverDate: undefined });
    } else if (active && !moment(active).isSame(day.date)) {
      this.setState({ hoverDate: day.date });
    }
  }

  getTime(activeMoment: Moment) {
    const hour = activeMoment.get('hour');
    const minute = activeMoment.get('minute');
    const second = activeMoment.get('second');

    return { hour, minute, second };
  }

  onDayClick(day: Day) {
    const {
      active,
      activeEnd,
      isRange,
      onViewValueChange,
      onChange,
      onDirectionChange,
    } = this.props;
    const activeMoment = moment(active);
    const { hour, minute, second } = this.getTime(activeMoment);
    const dayMoment = moment(day.date);
    // Preserve time in cases where Date and Time are being used together
    dayMoment.set({ hour: hour, minute: minute, second: second });
    const rangeIsSet = active && activeEnd;
    const noDatesSet = !active && !activeEnd;
    const range: { startDate: Date; endDate: Date | undefined } = {
      startDate: active || dayMoment.toDate(),
      endDate: activeEnd,
    };

    if (!isRange || rangeIsSet || noDatesSet) {
      range.startDate = dayMoment.toDate();
      range.endDate = undefined;
    } else if (activeMoment.isAfter(dayMoment)) {
      range.startDate = dayMoment.toDate();
      range.endDate = active;
      this.setState({ hoverDate: undefined });
    } else if (activeMoment.isSameOrBefore(dayMoment)) {
      range.endDate = dayMoment.toDate();
      this.setState({ hoverDate: undefined });
    }

    if (day.isPreviousMonth) {
      onDirectionChange(-1);
    } else if (day.isNextMonth) {
      onDirectionChange(1);
    }
    onViewValueChange(day.date);
    onChange(range);
  }

  onArrowClick(direction: -1 | 1) {
    const { viewValue, onViewValueChange, onDirectionChange } = this.props;

    onViewValueChange(moment(viewValue).add(direction, 'month').toDate());

    onDirectionChange(direction);
  }

  isActive(day: Day) {
    const { active, activeEnd, isRange } = this.props;

    if (isRange && active && activeEnd) {
      return moment(day.date).isBetween(active, activeEnd, 'day', '[]');
    } else if (active) {
      return moment(day.date).isSame(active, 'day');
    }

    return false;
  }

  isHover(day: Day) {
    const { hoverDate } = this.state;
    const { active } = this.props;

    if (!hoverDate) {
      return false;
    }

    if (moment(active).isBefore(hoverDate)) {
      return moment(day.date).isBetween(active, hoverDate, 'day', '(]');
    } else if (moment(active).isAfter(hoverDate)) {
      return moment(day.date).isBetween(hoverDate, active, 'day', '[)');
    }

    return false;
  }

  getRangeStartEnd() {
    const { active, activeEnd } = this.props;
    const { hoverDate } = this.state;
    const activeMoment = moment(active);
    const activeEndMoment = moment(activeEnd);
    const hoverDateMoment = moment(hoverDate);

    const rangeStartEnd: {
      start: moment.Moment;
      end: moment.Moment | undefined;
    } = {
      start: activeMoment,
      end: activeEndMoment,
    };

    if (!activeEnd) {
      if (!hoverDate) {
        rangeStartEnd.end = undefined;
      } else if (hoverDateMoment.isBefore(active)) {
        rangeStartEnd.start = moment(hoverDate);
        rangeStartEnd.end = activeMoment;
      } else if (hoverDateMoment.isAfter(active)) {
        rangeStartEnd.end = moment(hoverDate);
      }
    } else if (activeEndMoment.isBefore(active)) {
      rangeStartEnd.start = activeEndMoment;
      rangeStartEnd.end = activeMoment;
    }

    return rangeStartEnd;
  }

  getColor(day: Day) {
    const active = this.isActive(day);

    return day.isToday && !active ? 'error' : 'primary';
  }

  getBorder(day: Day) {
    return day.isToday || this.isActive(day) ? 'contained' : 'text';
  }

  isOutsideMinMax(day: Day) {
    const { minDate, maxDate } = this.props;

    if (minDate && maxDate) {
      return !moment(day.date).isBetween(minDate, maxDate);
    } else if (minDate) {
      return moment(day.date).isBefore(minDate);
    } else if (maxDate) {
      return moment(day.date).isAfter(maxDate);
    }

    return false;
  }

  renderDay(day: Day) {
    const isHover = this.isHover(day);
    const className = classNames(
      day.isPreviousMonth || day.isNextMonth ? css.isOtherMonth : ''
    );

    return (
      <Button
        className={className}
        color={isHover ? 'primary' : this.getColor(day)}
        data-sumo-test={`calendar-day-view-${day.dayOfMonth}${
          this.isOutsideMinMax(day) ? '-disabled' : ''
        }`}
        disabled={this.isOutsideMinMax(day)}
        key={day.date.toString()}
        onClick={() => this.onDayClick(day)}
        onMouseEnter={() => this.onDayHover(day)}
        onMouseLeave={bind(this.onDayLeave, this)}
        sx={{
          color: isHover ? 'primary' : this.getColor(day),
          minWidth: 'unset',
          height: 32,
          width: 42,
        }}
        size='small'
        variant={isHover ? 'outlined' : this.getBorder(day)}
      >
        {day.dayOfMonth}
      </Button>
    );
  }

  render() {
    const { viewValue, onViewChange, onChange } = this.props;
    const { activeIndex } = this.state;
    const weeks = getWeeks(viewValue);

    // Set locale to english always because we show full month names
    // And we don't want those to be translated by moment
    const viewMomentInEnglish = moment(viewValue).locale('en');

    return (
      <Fragment>
        <div className={css.header}>
          <IconButton
            size='medium'
            aria-label='Previous Month'
            data-sumo-test='calendar-day-view-previous-month-button'
            onClick={() => this.onArrowClick(-1)}
            sx={{ m: 0, p: 0, pt: 0.625, px: 0.625 }}
          >
            <PrevMonthIcon />
          </IconButton>
          <div className={css.monthYear}>
            <Button
              data-sumo-test='calendar-day-view-month-button'
              onClick={() => onViewChange('month')}
              size='small'
              variant='text'
            >
              {viewMomentInEnglish.format('MMMM')}
            </Button>
            <Button
              data-sumo-test='calendar-day-view-year-button'
              onClick={() => onViewChange('year')}
              size='small'
              variant='text'
            >
              {viewMomentInEnglish.format('YYYY')}
            </Button>
          </div>
          <IconButton
            size='medium'
            aria-label='Next Month'
            data-sumo-test='calendar-day-view-next-month-button'
            onClick={() => this.onArrowClick(1)}
            sx={{ m: 0, p: 0, pt: 0.625, px: 0.625 }}
          >
            <NextMonthIcon />
          </IconButton>
        </div>
        <CalendarNavigation
          activeIndex={activeIndex}
          className={css.allDays}
          numColumns={7}
          onEscape={() =>
            onChange({
              startDate: undefined,
              endDate: undefined,
            })
          }
          onIndexChange={bind(this.setFocus, this)}
        >
          {weeks.map((week) => week.map((day) => this.renderDay(day)))}
        </CalendarNavigation>
      </Fragment>
    );
  }
}
